import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { SilentTimer } from '../../components/SilentTimer';
import { SampleMeta, usePublicSample } from './shared';

/** Splits a narration script at its "..." pauses into readable lines. */
function splitTranscript(text: string | null): string[] {
  if (!text) return [];
  return text
    .split(/\s*(?:\.\.\.|…)+\s*/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => (/[.!?。！？।]$/.test(line) ? line : `${line}…`));
}

/** Review fix F11: the public sample practice -- transcript plus a silent timer, no account and no
 * audio. Served only by the dedicated GET /api/public/sample (never a protected endpoint), with
 * its honest review status and the language the text actually arrived in. */
export function PublicSample() {
  const { t } = useTranslation();
  const { status, sample, retry } = usePublicSample();

  if (status === 'loading') {
    return <p className="page-subtitle" role="status">{t('public.sample.loading')}</p>;
  }
  if (status === 'error' || !sample) {
    return (
      <div>
        <h1 className="page-title">{t('meta.titles.sample')}</h1>
        <p role="alert">{t('public.sample.error')}</p>
        <button type="button" className="btn btn-secondary" onClick={retry}>{t('public.sample.retry')}</button>
      </div>
    );
  }

  const lines = splitTranscript(sample.transcript);
  return (
    <article className="sample-page" aria-labelledby="sample-title">
      <p className="sample-card-eyebrow">{t('public.sample.eyebrow')}</p>
      <h1 id="sample-title" className="page-title" lang={sample.contentLanguage}>{sample.title}</h1>
      <p className="public-lead" lang={sample.contentLanguage}>{sample.description}</p>
      <SampleMeta sample={sample} />
      <p className="page-subtitle">{t('public.sample.intro')}</p>
      {sample.guidanceRequired && <p className="field-hint">{t('public.sample.guidance')}</p>}

      <div className="sample-layout">
        <SilentTimer durationSeconds={sample.durationSeconds} />
        <section className="sample-transcript" aria-labelledby="sample-words">
          <h2 id="sample-words">{t('public.sample.transcriptHeading')}</h2>
          <div lang={sample.contentLanguage}>
            {lines.map((line, i) => <p key={i}>{line}</p>)}
          </div>
        </section>
      </div>

      <section className="info-card sample-after" aria-labelledby="sample-after-heading">
        <h2 id="sample-after-heading">{t('public.sample.after.heading')}</h2>
        <p>{t('public.sample.after.body')}</p>
        <div className="landing-cta-row">
          <Link to="/signup" className="btn btn-primary">{t('public.sample.after.cta')}</Link>
          <Link to="/how-it-works" className="btn btn-secondary">{t('public.sample.after.secondary')}</Link>
        </div>
      </section>
    </article>
  );
}
